import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Check, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import api, { formatErr } from "../lib/api";
import Brand from "../components/Brand";

const RULES = [
  { label: "At least 10 characters",  test: (p) => p.length >= 10 },
  { label: "An uppercase letter",     test: (p) => /[A-Z]/.test(p) },
  { label: "A lowercase letter",      test: (p) => /[a-z]/.test(p) },
  { label: "A digit",                 test: (p) => /\d/.test(p) },
  { label: "A symbol",                test: (p) => /[!@#$%^&*(),.?":{}|<>_\-+=/[\]\\;']/.test(p) },
];

/**
 * Forced password change for admin-invited users.
 * Routed at `/change-password`; `<Protected>` redirects every
 * authenticated user with `must_change_password: true` here.
 */
export default function ChangePassword() {
  const { user, refresh } = useAuth();
  const [curr, setCurr]       = useState("");
  const [next, setNext]       = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState("");
  const [done, setDone]       = useState(false);
  const nav = useNavigate();

  const strengthOk = RULES.every(r => r.test(next));
  const match = next && next === confirm;
  const ok = curr && strengthOk && match;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: curr,
        new_password: next,
        confirm_password: confirm,
      });
      // Refresh user so must_change_password flips to false
      try { await refresh(); } catch (_) {}
      setDone(true);
      setTimeout(() => nav("/projects", { replace: true }), 800);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="pf-blueprint-bg" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div className="pf-card pf-grain" style={{ width: 460, padding: 32 }}>
        <Brand height={72} withTagline testId="change-password-brand"/>
        <div className="pf-overline" style={{ marginTop: 18, marginBottom: 6 }}>
          ACCOUNT · FIRST LOGIN
        </div>
        <h1 className="pf-heading" style={{ fontSize: 24, margin: "0 0 6px" }}>
          Set your password
        </h1>
        <p style={{ color: "var(--pf-text-muted)", fontSize: 13,
                     marginTop: 0, marginBottom: 18 }}>
          Hi {user?.name || user?.email} — please change the temporary
          password your administrator sent before you continue.
        </p>

        <form onSubmit={submit} noValidate>
          <label className="pf-label">TEMPORARY PASSWORD</label>
          <input className="pf-input" type="password" required
                  autoComplete="current-password"
                  value={curr} onChange={(e) => setCurr(e.target.value)}
                  data-testid="cp-current"/>

          <div style={{ height: 12 }}/>
          <label className="pf-label">NEW PASSWORD</label>
          <input className="pf-input" type="password" required minLength={10}
                  autoComplete="new-password"
                  value={next} onChange={(e) => setNext(e.target.value)}
                  data-testid="cp-new"/>
          <div data-testid="cp-rules"
                style={{ marginTop: 6, fontSize: 11,
                          fontFamily: "'JetBrains Mono', monospace",
                          color: "var(--pf-text-muted)" }}>
            {RULES.map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6,
                                      color: r.test(next) ? "#22c55e" : "var(--pf-text-muted)" }}>
                <Check size={11}/> {r.label}
              </div>
            ))}
          </div>

          <div style={{ height: 12 }}/>
          <label className="pf-label">CONFIRM NEW PASSWORD</label>
          <input className="pf-input" type="password" required minLength={10}
                  autoComplete="new-password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="cp-confirm"/>
          {confirm && !match && (
            <div style={{ fontSize: 11, color: "var(--pf-err)", marginTop: 6 }}
                  data-testid="cp-mismatch">
              Passwords do not match
            </div>
          )}

          {err && <div className="pf-sev pf-sev-error"
                        data-testid="cp-error"
                        style={{ display: "block", marginTop: 14, padding: "8px 10px" }}>
            {err}
          </div>}
          {done && <div className="pf-sev pf-sev-ok" data-testid="cp-done"
                          style={{ display: "block", marginTop: 14, padding: "8px 10px",
                                    color: "#22c55e",
                                    border: "1px solid #22c55e", borderRadius: 4 }}>
            Password updated — redirecting…
          </div>}

          <button type="submit" className="btn btn-primary"
                  style={{ marginTop: 18, width: "100%" }}
                  disabled={busy || !ok}
                  data-testid="cp-submit">
            {busy ? <><Loader2 size={12} className="pf-spin"/> Saving…</>
              : <><KeyRound size={12}/> Update password <ArrowRight size={12}/></>}
          </button>
        </form>
      </div>
    </div>
  );
}
